import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Star, 
  Users, 
  ArrowLeft,
  Eye,
  EyeOff,
  Timer,
  Award,
  Sparkles,
  Trophy,
  Lock,
  Unlock,
  Maximize2
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import { databaseService } from '@/services/database'
import { realtimeService } from '@/services/realtime'
import { Room, Player, Prompt, Vote, GameRound } from '@/types'
import { trackEvent } from '@/services/analytics'
import ReportContentButton from '@/components/ReportContentButton'
import FullscreenImageModal from '@/components/FullscreenImageModal'
import { useTranslation } from 'react-i18next'
import RoomClosedModal from '@/components/RoomClosedModal'

export default function VotingPage() {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  
  const [room, setRoom] = useState<Room | null>(null)
  const [currentPlayer, setCurrentPlayer] = useState<Player | null>(null)
  const [prompts, setPrompts] = useState<Prompt[]>([])
  const [rounds, setRounds] = useState<GameRound[]>([])
  const [votes, setVotes] = useState<Vote[]>([])
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0)
  const [selectedScore, setSelectedScore] = useState<number | null>(null)
  const [timeLeft, setTimeLeft] = useState(0)
  const [votingStartedAt, setVotingStartedAt] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [showPromptText, setShowPromptText] = useState(false)
  const [currentVotes, setCurrentVotes] = useState<{voterId: string, voterName: string, score: number}[]>([])
  const [previewVotes, setPreviewVotes] = useState<{voterId: string, voterName: string, score: number}[]>([])
  const DEFAULT_TIMEOUT_SCORE = 3
  const [showRoomClosed, setShowRoomClosed] = useState(false)
  const [showFullscreenImage, setShowFullscreenImage] = useState(false)

  const amIHost = () => {
    try {
      if (currentPlayer && typeof currentPlayer.isHost === 'boolean') return !!currentPlayer.isHost
      const saved = localStorage.getItem('currentPlayer')
      if (!saved) return false
      const me = JSON.parse(saved)
      return !!me?.isHost
    } catch {
      return !!currentPlayer?.isHost
    }
  }

  // Refs to avoid stale closures in realtime handlers
  const currentPromptIdRef = useRef<string | null>(null)
  const currentPlayerIdRef = useRef<string | null>(null)
  const votingChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null)

  useEffect(() => {
    if (!roomId) {
      navigate('/')
      return
    }

    // Get current player from localStorage
    const savedPlayer = localStorage.getItem('currentPlayer')
    if (savedPlayer) {
      setCurrentPlayer(JSON.parse(savedPlayer))
    }

    loadData()
    const cleanupRealtime = setupRealtimeSubscription()
    
    return () => {
      if (cleanupRealtime) {
        cleanupRealtime()
      }
      // Note: Don't cleanup room here as it causes issues with rapid page transitions
      // Cleanup will happen when user actually leaves the room via buttons
    }
  }, [roomId])

  // Host presence kontrolü: host ayrılırsa modalı aç
  useEffect(() => {
    if (!roomId) return
    const playerJson = localStorage.getItem('currentPlayer')
    const me: Player | null = playerJson ? JSON.parse(playerJson) : null
    if (!me) return

    realtimeService.onPresenceSync(roomId, () => {
      // Host offline olsa bile; voting sırasında modal göstermeyelim
    })
  }, [roomId, room?.players])

  // Join realtime room for live previews/casts
  useEffect(() => {
    if (!roomId || !currentPlayer) return
    currentPlayerIdRef.current = currentPlayer.id
    const isHost = !!room?.players.find(p => p.id === currentPlayer.id)?.isHost
    const isReady = !!room?.players.find(p => p.id === currentPlayer.id)?.isReady
    realtimeService.joinRoom(roomId, {
      id: currentPlayer.id,
      name: currentPlayer.name,
      isHost,
      isReady: !!isReady
    })

    // Listen vote_cast via realtimeService with refs
    realtimeService.on(roomId, 'vote_cast', (payload: any) => {
      if (payload.promptId && payload.promptId === currentPromptIdRef.current && payload.voterId !== currentPlayerIdRef.current) {
        const newVote = {
          voterId: payload.voterId,
          voterName: payload.voterName,
          score: payload.score
        }
        setCurrentVotes(prev => {
          const exists = prev.some(v => v.voterId === payload.voterId)
          if (exists) return prev
          return [...prev, newVote]
        })
        setPreviewVotes(prev => prev.filter(v => v.voterId !== payload.voterId))
      }
    })

    // Listen vote_preview via realtimeService with refs
    realtimeService.on(roomId, 'vote_preview', (payload: any) => {
      if (payload.promptId && payload.promptId === currentPromptIdRef.current && payload.voterId !== currentPlayerIdRef.current) {
        setPreviewVotes(prev => {
          const updated = { voterId: payload.voterId, voterName: payload.voterName, score: payload.score }
          const exists = prev.some(v => v.voterId === payload.voterId)
          if (exists) return prev.map(v => v.voterId === payload.voterId ? updated : v)
          return [...prev, updated]
        })
      }
    })


    // Cleanup handled by outer leaveRoom
  }, [roomId, currentPlayer?.id])

  // Server-side timer effect
  useEffect(() => {
    if (!room || room.status !== 'voting') return

    const updateTimeFromServer = async () => {
      try {
        const { timeLeft, isExpired } = await databaseService.getRemainingTime(room.id)
        setTimeLeft(timeLeft)

        if (isExpired) {
          // Server says time is expired, but database will handle progression
          console.log('Voting time expired')
        }
      } catch (error) {
        console.error('Error getting server time:', error)
      }
    }

    // Update immediately
    updateTimeFromServer()

    // Then update every second
    const interval = setInterval(updateTimeFromServer, 1000)

    return () => clearInterval(interval)
  }, [room?.id, room?.status])

  // Reset votes for new prompt and load existing votes from DB
  useEffect(() => {
    if (room && room.status === 'voting' && prompts.length > 0) {
      const currentPrompt = prompts[currentPromptIndex]
      if (currentPrompt) {
        currentPromptIdRef.current = currentPrompt.id
        const promptVotes = votes.filter(v => v.promptId === currentPrompt.id)
        const currentPromptVotes = promptVotes.map(v => {
          const voter = room.players.find(p => p.id === v.voterId)
          return {
            voterId: v.voterId,
            voterName: voter?.name || 'Unknown',
            score: v.score
          }
        })
        setCurrentVotes(currentPromptVotes)
        setPreviewVotes([])
      } else {
        setCurrentVotes([])
        setPreviewVotes([])
        currentPromptIdRef.current = null
      }
    }
  }, [currentPromptIndex, votes, prompts, room?.players, room?.status])

  useEffect(() => {
    // Auto-submit default score on timeout if not voted
    const currentPrompt = prompts[currentPromptIndex]
    const isCurrentPromptMine = currentPrompt && currentPlayer && currentPrompt.playerId === currentPlayer.id
    
    if (
      timeLeft === 0 &&
      room &&
      room.status === 'voting' &&
      currentPromptIndex < prompts.length &&
      !submitting &&
      !hasVotedForCurrentPrompt() &&
      !isCurrentPromptMine &&
      votingStartedAt && // Only if voting actually started
      (Date.now() - votingStartedAt) > 10000 // And at least 10 seconds have passed
    ) {
      console.log('VotingPage: Auto-submitting timeout vote after', Date.now() - votingStartedAt, 'ms')
      submitVote(DEFAULT_TIMEOUT_SCORE)
    }
  }, [timeLeft, prompts, currentPromptIndex, currentPlayer, votingStartedAt])

  useEffect(() => {
    // Check if all players have voted for all prompts they can vote for
    if (room && prompts.length > 0 && votes.length > 0) {
      // Filter votes to only include those for current prompts (avoid stale votes)
      const currentPromptIds = new Set(prompts.map(p => p.id))
      const filteredVotes = votes.filter(vote => currentPromptIds.has(vote.promptId))
      
      console.log('Checking voting completion...', { 
        totalPrompts: prompts.length, 
        allVotes: votes.length,
        filteredVotes: filteredVotes.length,
        players: room.players.length 
      })
      
      // Calculate how many votes each player should give
      let totalVotesNeeded = 0
      room.players.forEach(player => {
        // Each player votes for all prompts except their own
        const playerPromptsCount = prompts.filter(p => p.playerId === player.id).length
        const promptsToVoteFor = prompts.length - playerPromptsCount
        totalVotesNeeded += promptsToVoteFor
        console.log(`Player ${player.name}: ${promptsToVoteFor} votes needed (${prompts.length} total - ${playerPromptsCount} own)`)
      })
      
      console.log(`Total votes needed: ${totalVotesNeeded}, current filtered votes: ${filteredVotes.length}`)
      
      if (filteredVotes.length >= totalVotesNeeded) {
        console.log('All votes completed, moving to results')
        // Move to results phase
        navigate(`/results/${roomId}`)
      }
    }
  }, [votes, room, prompts, roomId, navigate])

  const loadData = async () => {
    try {
      const roomData = await databaseService.getRoom(roomId!)
      setRoom(roomData)
      if (roomData.status === 'finished') {
        if (!amIHost()) setShowRoomClosed(true)
        return
      }
      
      if (roomData.status === 'results') {
        navigate(`/results/${roomId}`)
        return
      }

      // Load prompts for ALL rounds since we're in voting phase
      const allRoundsPrompts = await databaseService.getAllRoundPrompts(roomId!)
      const completedPrompts = allRoundsPrompts
        .filter(p => p.imageUrl && (p.imageGenerationStatus === 'completed' || p.imageGenerationStatus === 'failed'))
        .sort((a, b) => (a.roundNumber - b.roundNumber) || (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()))

      console.log('Loaded prompts for voting:', {
        currentRound: roomData.roundNumber,
        totalPrompts: allRoundsPrompts.length,
        completedPrompts: completedPrompts.length,
        prompts: completedPrompts.map(p => ({ 
          id: p.id, 
          playerId: p.playerId, 
          roundNumber: p.roundNumber,
          status: p.imageGenerationStatus,
          hasUrl: !!p.imageUrl
        }))
      })

      // Don't filter out own prompts - show all prompts but disable voting for own
      setPrompts(completedPrompts)

      // If no prompts at all, go to results
      if (completedPrompts.length === 0) {
        console.log('No completed prompts found, going to results')
        navigate(`/results/${roomId}`)
        return
      }

      // Load rounds to get word information
      const allRounds = await databaseService.getAllRounds(roomId!)
      setRounds(allRounds)

      // Load votes for all rounds since we're voting on all prompts
      const allRoundsVotes = await databaseService.getAllRoundVotes(roomId!)
      
      console.log('Loaded votes for all rounds:', {
        currentRound: roomData.roundNumber,
        allVotesCount: allRoundsVotes.length,
        currentPromptIds: completedPrompts.map(p => p.id),
        votes: allRoundsVotes.map(v => ({ 
          id: v.id, 
          voterId: v.voterId, 
          promptId: v.promptId, 
          score: v.score,
          isForCurrentPrompts: completedPrompts.some(p => p.id === v.promptId)
        }))
      })
      
      // Don't filter here - let the individual useEffects handle filtering based on current state
      setVotes(allRoundsVotes)

      // If votingStartedAt is not set and game is in voting, set it to now
      // This handles page refresh scenarios
      if (roomData.status === 'voting' && !votingStartedAt) {
        console.log('VotingPage: Setting votingStartedAt to now for fresh voting session')
        setVotingStartedAt(Date.now())
        // Also reset timeLeft to prevent immediate timeout
        setTimeLeft(roomData.settings.votingTimeLimit || 180)
      }
    } catch (error: any) {
      setError(error.message || t('voting.errorUnknown'))
    } finally {
      setLoading(false)
    }
  }

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel(`voting-${roomId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'rooms',
        filter: `id=eq.${roomId}`
      }, (payload) => {
        console.log('Voting room changed:', payload)
        const dbRoom: any = (payload as any).new
        if (dbRoom && dbRoom.id === roomId) {
          if (dbRoom.status === 'finished') {
            if (!amIHost()) setShowRoomClosed(true)
            return
          }
          // Immediately handle status changes
          if (dbRoom.status === 'results') {
            console.log('Room status changed to results via realtime, navigating immediately')
            navigate(`/results/${roomId}`)
            return
          }
          
          // For other changes, reload data
          setTimeout(() => {
            loadData()
          }, 500)
        }
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'votes',
        filter: `room_id=eq.${roomId}`
      }, (payload) => {
        console.log('Vote changed:', payload)
        // Refresh votes for all rounds, don't reload entire data to avoid timer reset
        // Don't filter here as prompts state might be stale - filtering will happen in useEffect
        if (roomId) {
          databaseService.getAllRoundVotes(roomId).then(allVotes => {
            console.log('Real-time vote update: loaded', allVotes.length, 'votes, filtering will happen in useEffect')
            setVotes(allVotes) // Set all votes, filtering will happen in useEffect based on current prompts
          }).catch(console.error)
        }
      })
      .on('broadcast', { event: 'vote_cast' }, ({ payload }) => {
        console.log('Vote cast broadcast received:', payload)
        // Only update if this vote is for the current prompt and not from current user
        if (payload.promptId === prompts[currentPromptIndex]?.id && payload.voterId !== currentPlayer?.id) {
          const newVote = {
            voterId: payload.voterId,
            voterName: payload.voterName,
            score: payload.score
          }
          setCurrentVotes(prev => {
            // Check if vote already exists to avoid duplicates
            const exists = prev.some(v => v.voterId === payload.voterId)
            if (exists) return prev
            return [...prev, newVote]
          })
          // Remove preview if exists
          setPreviewVotes(prev => prev.filter(v => v.voterId !== payload.voterId))
        }
      })
      .on('broadcast', { event: 'vote_preview' }, ({ payload }) => {
        // Show live preview for other users (not self) and only for current prompt
        const matchesPrompt = payload.promptId === currentPromptIdRef.current
          || payload.promptIndex === currentPromptIndex
        const isOtherUser = payload.voterId !== currentPlayerIdRef.current
        if (matchesPrompt && isOtherUser) {
          setPreviewVotes(prev => {
            const updated = { voterId: payload.voterId, voterName: payload.voterName, score: payload.score }
            const exists = prev.some(v => v.voterId === payload.voterId)
            if (exists) return prev.map(v => v.voterId === payload.voterId ? updated : v)
            return [...prev, updated]
          })
        }
      })
      .subscribe()

    votingChannelRef.current = channel

    return () => {
      try {
        supabase.removeChannel(channel)
      } catch (error) {
        console.log('WebSocket cleanup completed')
      }
      votingChannelRef.current = null
    }
  }



  // Database will handle automatic progression, we just listen for changes
  useEffect(() => {
    if (room && room.status === 'results') {
      // Database automatically moved to results, navigate
      navigate(`/results/${roomId}`)
    }
  }, [room?.status, roomId, navigate])

  // Listen for current voting prompt changes from database
  useEffect(() => {
    if (room?.currentVotingPromptId && prompts.length > 0) {
      const newPromptIndex = prompts.findIndex(p => p.id === room.currentVotingPromptId)
      if (newPromptIndex !== -1 && newPromptIndex !== currentPromptIndex) {
        setCurrentPromptIndex(newPromptIndex)
        setCurrentVotes([])
        setSelectedScore(null)
        setShowPromptText(false)
        setVotingStartedAt(Date.now()) // Reset timer for new prompt
      }
    }
  }, [room?.currentVotingPromptId, prompts, currentPromptIndex])



  const submitVote = async (overrideScore?: number) => {
    const scoreToUse = overrideScore ?? selectedScore ?? null
    if (!currentPlayer || !scoreToUse || !prompts[currentPromptIndex]) return

    setSubmitting(true)
    setError('')

    try {
      await databaseService.submitVote(
        currentPlayer.id,
        prompts[currentPromptIndex].id,
        room!.id,
        prompts[currentPromptIndex].roundNumber,
        scoreToUse
      )
      
      // Analytics: oy gonderimi
      trackEvent({
        event: 'scraible_vote_submit',
        properties: {
          room_id: room!.id,
          round_number: prompts[currentPromptIndex].roundNumber,
          prompt_id: prompts[currentPromptIndex].id,
          score: scoreToUse,
          auto_timeout: !!overrideScore && overrideScore === DEFAULT_TIMEOUT_SCORE,
        },
      })

      // Immediately add vote to local state for instant UI update
      const newVote = {
        voterId: currentPlayer.id,
        voterName: currentPlayer.name,
        score: scoreToUse
      }
      setCurrentVotes(prev => [...prev, newVote])
      
      // Broadcast vote with proper data structure
      if (roomId) {
        await realtimeService.broadcast(roomId, 'vote_cast', {
          voterId: currentPlayer.id,
          voterName: currentPlayer.name,
          promptId: prompts[currentPromptIndex].id,
          score: scoreToUse,
          promptIndex: currentPromptIndex
        })
      }

      setSelectedScore(null)
      // Clear my local preview once confirmed
      setPreviewVotes(prev => prev.filter(v => v.voterId !== currentPlayer.id))
      
      // Host will auto-advance via useEffect when currentVotes updates
    } catch (error: any) {
      setError(error.message || 'Oy verilirken hata oluştu')
    } finally {
      setSubmitting(false)
    }
  }



  const hasVotedForCurrentPrompt = () => {
    if (!currentPlayer || !prompts[currentPromptIndex]) return false
    return votes.some(v => 
      v.voterId === currentPlayer.id && 
      v.promptId === prompts[currentPromptIndex].id
    )
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getVotingProgress = () => {
    if (!currentPlayer || !room) return { current: 0, total: 0 }
    const myVotes = votes.filter(v => v.voterId === currentPlayer.id)
    return { current: myVotes.length, total: prompts.length }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-white/20 border-t-amber-400 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-20 h-20 border-2 border-amber-400/30 rounded-full animate-pulse mx-auto"></div>
            <div className="absolute inset-4 w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full animate-pulse"></div>
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-gradient-gold">{t('voting.loadingTitle')}</h3>
            <p className="text-white/80">{t('voting.loadingSubtitle')}</p>
            <div className="flex items-center justify-center space-x-2">
              <Star className="w-5 h-5 text-amber-400 animate-pulse" />
              <Trophy className="w-4 h-4 text-orange-400 animate-bounce" />
              <Award className="w-5 h-5 text-yellow-400 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !room) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-6">
          <div className="card-gradient max-w-md mx-auto border-red-400/50 bg-red-500/10">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center">
                <Award className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-red-400 mb-4">{t('voting.errorTitle')}</h2>
            <p className="text-white/80 mb-6">{error || t('voting.errorUnknown')}</p>
            <motion.button
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary"
            >
              {t('voting.backHome')}
            </motion.button>
          </div>
        </div>
      </div>
    )
  }

  const progress = getVotingProgress()
  const currentPrompt = prompts[currentPromptIndex]
  const currentPromptOwner = currentPrompt ? room.players.find(p => p.id === currentPrompt.playerId) : null
  const currentPromptRound = currentPrompt ? rounds.find(r => r.roundNumber === currentPrompt.roundNumber) : null
  const hasVoted = hasVotedForCurrentPrompt()
  const allVotingComplete = currentPromptIndex >= prompts.length
  const isMyPrompt = currentPrompt && currentPlayer && currentPrompt.playerId === currentPlayer.id

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative"
      >
        {/* Mobile: back (left), title (center), timer (right) */}
        <div className="sm:hidden px-4 mb-3">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <motion.button
                onClick={() => navigate(`/game/${roomId}`)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{t('voting.back')}</span>
              </motion.button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="flex items-center justify-center space-x-2"
              >
                <Trophy className="w-4 h-4 text-amber-400" />
                <h1 className="text-xl font-bold text-gradient-gold">{t('voting.header')}</h1>
                <Award className="w-4 h-4 text-orange-400" />
              </motion.div>
            </div>
            <div className="flex-shrink-0">
              <div className={`flex items-center space-x-2 px-3 py-2 rounded-xl backdrop-blur-sm border ${
                timeLeft < 60 
                  ? 'bg-red-500/20 border-red-400/50 text-red-400' 
                  : 'bg-amber-500/20 border-amber-400/50 text-amber-400'
              }`}>
                <Timer className="w-4 h-4" />
                <span className="font-mono text-base font-bold">
                  {formatTime(timeLeft)}
                </span>
              </div>
            </div>
          </div>
          {/* Mobile subtitle */}
          <div className="mt-1 flex items-center justify-center space-x-2">
            <Star className="w-3 h-3 text-yellow-400" />
            <p className="text-white/80 font-medium text-xs">
              {progress.current}/{progress.total} oy verildi
            </p>
            <Sparkles className="w-3 h-3 text-amber-400" />
          </div>
        </div>

        {/* Desktop header remains three columns */}
        <div className="hidden sm:flex items-center justify-between px-4">
          <motion.button
            onClick={() => navigate(`/game/${roomId}`)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-secondary flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('voting.back')}</span>
          </motion.button>
          
          <div className="text-center flex-1">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="flex items-center justify-center space-x-2 mb-1"
            >
              <Trophy className="w-6 h-6 text-amber-400" />
              <h1 className="text-3xl font-bold text-gradient-gold">{t('voting.header')}</h1>
              <Award className="w-6 h-6 text-orange-400" />
            </motion.div>
            <div className="flex items-center justify-center space-x-2">
              <Star className="w-4 h-4 text-yellow-400" />
              <p className="text-white/80 font-medium text-base">
                {progress.current}/{progress.total} oy verildi
              </p>
              <Sparkles className="w-4 h-4 text-amber-400" />
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl backdrop-blur-sm border ${
              timeLeft < 60 
                ? 'bg-red-500/20 border-red-400/50 text-red-400' 
                : 'bg-amber-500/20 border-amber-400/50 text-amber-400'
            }`}>
              <Timer className="w-5 h-5" />
              <span className="font-mono text-lg font-bold">
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Progress Bar removed */}

      {!allVotingComplete && currentPrompt ? (
        <>
          {/* Current Image - Enhanced Professional Display */}
          <motion.div
            key={currentPromptIndex}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="relative overflow-hidden"
          >
            {/* Header Card - Mobile Optimized */}
            <div className="card-gradient mb-4">
              {/* Mobile: Stack vertically */}
              <div className="sm:hidden">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold text-white">
                      {t('voting.image', { index: currentPromptIndex + 1, total: prompts.length })}
                    </h2>
                    <p className="text-white/70 text-xs">
                      {currentPromptOwner ? t('voting.ownerImage', { name: currentPromptOwner.name }) : ''}
                    </p>
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 py-2 rounded-xl border border-white/20">
                  <div className="text-white font-medium text-sm">
                    {t('voting.roundWord', { round: currentPrompt?.roundNumber, word: currentPromptRound?.word })}
                  </div>
                  <div className="text-white/60 text-xs mt-1">
                    {t('voting.coordinatedBy', { name: room?.players.find(p => p.isHost)?.name })}
                  </div>
                </div>
              </div>
              
              {/* Desktop: Side by side */}
              <div className="hidden sm:flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {t('voting.image', { index: currentPromptIndex + 1, total: prompts.length })}
                    </h2>
                    <p className="text-white/70 text-sm">
                      {currentPromptOwner ? t('voting.ownerImage', { name: currentPromptOwner.name }) : ''}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="bg-white/10 backdrop-blur-sm px-3 py-2 rounded-xl border border-white/20">
                    <div className="text-white font-medium text-sm">
                      {t('voting.roundWord', { round: currentPrompt?.roundNumber, word: currentPromptRound?.word })}
                    </div>
                    <div className="text-white/60 text-xs mt-1">
                      {t('voting.coordinatedBy', { name: room?.players.find(p => p.isHost)?.name })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Image Display */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 to-purple-500/20 rounded-3xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity duration-300"></div>
              <div className="relative bg-white/5 backdrop-blur-sm rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
                <img
                  src={currentPrompt.imageUrl}
                  alt={`Prompt ${currentPromptIndex + 1}`}
                  className="w-full h-80 md:h-96 lg:h-[500px] object-cover transition-transform duration-300 group-hover:scale-105"
                />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Bottom Right Actions */}
                <div className="absolute bottom-4 right-4 flex space-x-2 z-10">
                  <ReportContentButton roomId={room.id} promptId={currentPrompt.id} imageUrl={currentPrompt.imageUrl || undefined} compact />
                  <motion.button
                    onClick={() => setShowFullscreenImage(true)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-10 h-10 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                    title="View fullscreen"
                  >
                    <Maximize2 className="w-5 h-5" />
                  </motion.button>
                </div>
                
                {/* Prompt Text Overlay - Redesigned */}
                <div className="absolute bottom-4 left-4 right-4">
                  <motion.button
                    onClick={() => setShowPromptText(!showPromptText)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center space-x-2 bg-white/10 backdrop-blur-md text-white px-4 py-3 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 shadow-lg"
                  >
                    {showPromptText ? (
                      <>
                        <EyeOff className="w-5 h-5" />
                        <span className="font-medium">{t('voting.hidePrompt')}</span>
                      </>
                    ) : (
                      <>
                        <Eye className="w-5 h-5" />
                        <span className="font-medium">{t('voting.showPrompt')}</span>
                      </>
                    )}
                  </motion.button>
                  
                  {showPromptText && (
                    <motion.div
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -20, scale: 0.95 }}
                      className="mt-3 bg-black/80 backdrop-blur-md text-white p-4 rounded-2xl border border-white/10 shadow-xl"
                    >
                      <div className="flex items-start space-x-3">
                        <Sparkles className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                        <p className="text-white font-medium leading-relaxed">"{currentPrompt.text}"</p>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Enhanced Rating System */}
          {!hasVoted && !isMyPrompt && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card-gradient relative overflow-hidden"
            >
              {/* Background glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-yellow-500/10 animate-pulse"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white text-center">{t('voting.rateTitle')}</h3>
                  <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl flex items-center justify-center">
                    <Star className="w-5 h-5 text-white fill-current" />
                  </div>
                </div>
                
                {/* Mobile: Compact stars */}
                <div className="sm:hidden flex items-center justify-center gap-2 mb-4">
                  {[1, 2, 3, 4, 5].map((score) => (
                    <motion.button
                      key={score}
                      onClick={() => {
                        setSelectedScore(score)
                        // Locally show my own preview immediately
                        if (currentPlayer) {
                          setPreviewVotes(prev => {
                            const updated = { voterId: currentPlayer.id, voterName: currentPlayer.name, score }
                            const exists = prev.some(v => v.voterId === currentPlayer.id)
                            if (exists) return prev.map(v => v.voterId === currentPlayer.id ? updated : v)
                            return [...prev, updated]
                          })
                        }
                        // Broadcast preview selection to others (use both room-wide channel and page channel for robustness)
                        if (roomId && currentPlayer && prompts[currentPromptIndex]) {
                          const payload = {
                            voterId: currentPlayer.id,
                            voterName: currentPlayer.name,
                            promptId: prompts[currentPromptIndex].id,
                            score,
                            promptIndex: currentPromptIndex
                          }
                          // Room-scoped channel (realtime service)
                          realtimeService.broadcast(roomId, 'vote_preview', payload)
                          // Page-scoped channel (voting-<roomId>) to ensure delivery to listeners bound there
                          try {
                            votingChannelRef.current?.send({ type: 'broadcast', event: 'vote_preview', payload })
                          } catch {}
                        }
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`relative p-2 rounded-xl border-2 transition-all duration-300 group ${
                        selectedScore === score
                          ? 'bg-gradient-to-br from-amber-500 to-orange-600 border-amber-400 shadow-lg shadow-amber-500/25'
                          : 'bg-white/10 backdrop-blur-sm border-white/20 hover:border-amber-400/50 hover:bg-white/20'
                      }`}
                    >
                      {selectedScore === score && (
                        <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
                      )}
                      <Star
                        className={`relative w-8 h-8 transition-all duration-300 ${ 
                          (selectedScore ?? 0) >= score 
                            ? 'text-white fill-current drop-shadow-lg' 
                            : 'text-white/60 group-hover:text-amber-300'
                        }`}
                      />
                    </motion.button>
                  ))}
                </div>
                
                {/* Desktop: Larger stars */}
                <div className="hidden sm:flex items-center justify-center gap-3 mb-6">
                  {[1, 2, 3, 4, 5].map((score) => (
                    <motion.button
                      key={score}
                      onClick={() => {
                        setSelectedScore(score)
                        // Locally show my own preview immediately
                        if (currentPlayer) {
                          setPreviewVotes(prev => {
                            const updated = { voterId: currentPlayer.id, voterName: currentPlayer.name, score }
                            const exists = prev.some(v => v.voterId === currentPlayer.id)
                            if (exists) return prev.map(v => v.voterId === currentPlayer.id ? updated : v)
                            return [...prev, updated]
                          })
                        }
                        // Broadcast preview selection to others (use both room-wide channel and page channel for robustness)
                        if (roomId && currentPlayer && prompts[currentPromptIndex]) {
                          const payload = {
                            voterId: currentPlayer.id,
                            voterName: currentPlayer.name,
                            promptId: prompts[currentPromptIndex].id,
                            score,
                            promptIndex: currentPromptIndex
                          }
                          // Room-scoped channel (realtime service)
                          realtimeService.broadcast(roomId, 'vote_preview', payload)
                          // Page-scoped channel (voting-<roomId>) to ensure delivery to listeners bound there
                          try {
                            votingChannelRef.current?.send({ type: 'broadcast', event: 'vote_preview', payload })
                          } catch {}
                        }
                      }}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      whileTap={{ scale: 0.95 }}
                      className={`relative p-4 rounded-2xl border-2 transition-all duration-300 group ${
                        selectedScore === score
                          ? 'bg-gradient-to-br from-amber-500 to-orange-600 border-amber-400 shadow-lg shadow-amber-500/25'
                          : 'bg-white/10 backdrop-blur-sm border-white/20 hover:border-amber-400/50 hover:bg-white/20'
                      }`}
                    >
                      {selectedScore === score && (
                        <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
                      )}
                      <Star
                        className={`relative w-10 h-10 transition-all duration-300 ${ 
                          (selectedScore ?? 0) >= score 
                            ? 'text-white fill-current drop-shadow-lg' 
                            : 'text-white/60 group-hover:text-amber-300'
                        }`}
                      />
                      <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 text-xs font-bold text-white opacity-0 group-hover:opacity-100 transition-opacity">
                        {score}
                      </div>
                    </motion.button>
                  ))}
                </div>

                {selectedScore && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center mb-4"
                  >
                    <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl border border-white/20">
                      <Star className="w-5 h-5 text-amber-400 fill-current" />
                      <p className="text-white font-medium">{t('voting.selectedScore', { score: selectedScore })}</p>
                    </div>
                  </motion.div>
                )}

                <div className="flex justify-center">
                  <motion.button
                    onClick={() => submitVote()}
                    disabled={!selectedScore || submitting || hasVoted}
                    whileHover={{ scale: selectedScore ? 1.05 : 1 }}
                    whileTap={{ scale: selectedScore ? 0.95 : 1 }}
                    className={`px-6 py-3 sm:px-8 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 sm:space-x-3 ${
                      hasVoted 
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-500/25' 
                        : selectedScore
                          ? 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg shadow-violet-500/25'
                          : 'bg-white/10 backdrop-blur-sm border border-white/20 text-white/60'
                    }`}
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>{t('voting.voting')}</span>
                      </>
                    ) : hasVoted ? (
                      <>
                        <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
                        <span>{t('voting.voted')}</span>
                      </>
                    ) : (
                      <>
                        <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>{t('voting.vote')}</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Enhanced Live Preview Card */}
          <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="card-gradient relative overflow-hidden"
            >
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-blue-500/10 to-purple-500/10 animate-pulse"></div>
              
              <div className="relative z-10">
                {/* Mobile: Stack header */}
                <div className="sm:hidden mb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-white">{t('voting.livePreviewsTitle', { count: previewVotes.length })}</h4>
                      <p className="text-white/70 text-xs">{t('voting.livePreviewsSubtitle')}</p>
                    </div>
                  </div>
                  {room && (
                    <div className="bg-white/10 backdrop-blur-sm px-3 py-2 rounded-xl border border-white/20">
                      <div className="text-white/90 text-sm font-medium text-center">
                        {t('voting.remainingVoters', { count: room.players.filter(p => p.id !== currentPrompt?.playerId).length - currentVotes.length })}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Desktop: Side by side header */}
                <div className="hidden sm:flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white">{t('voting.livePreviewsTitle', { count: previewVotes.length })}</h4>
                      <p className="text-white/70 text-sm">{t('voting.livePreviewsSubtitle')}</p>
                    </div>
                  </div>
                  {room && (
                    <div className="bg-white/10 backdrop-blur-sm px-3 py-2 rounded-xl border border-white/20">
                      <div className="text-white/90 text-sm font-medium">
                        {t('voting.remainingVoters', { count: room.players.filter(p => p.id !== currentPrompt?.playerId).length - currentVotes.length })}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="space-y-3">
                  {currentVotes.length === 0 && previewVotes.length === 0 && (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                        <Star className="w-8 h-8 text-white/50" />
                      </div>
                      <p className="text-white/70">{t('voting.livePreviewsEmpty')}</p>
                    </div>
                  )}
                  
                  {/* Confirmed votes - Mobile optimized */}
                  {currentVotes.map((cv, index) => (
                    <motion.div 
                      key={`c-${cv.voterId || index}`} 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-sm border border-green-400/30 rounded-xl px-3 py-2 shadow-lg"
                    >
                      <div className="flex items-center min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold mr-2 shadow-lg text-sm">
                          {(cv.voterName?.charAt(0) || '?').toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-white font-medium truncate text-sm">{cv.voterName}</span>
                            <Lock className="w-3 h-3 text-green-400 flex-shrink-0" />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center flex-shrink-0 space-x-1">
                        <div className="flex items-center">
                          {[1,2,3,4,5].map(star => (
                            <Star
                              key={star}
                              className={`w-3 h-3 sm:w-4 sm:h-4 ${star <= cv.score ? 'text-yellow-400 fill-current' : 'text-white/30'}`}
                            />
                          ))}
                        </div>
                        <span className="text-sm sm:text-base font-bold text-green-300 bg-green-500/20 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-xs sm:text-sm">{cv.score}</span>
                      </div>
                    </motion.div>
                  ))}
                  
                  {/* Previews - Mobile optimized */}
                  {previewVotes.filter(pv => !currentVotes.some(cv => cv.voterId === pv.voterId)).map((pv, index) => (
                    <motion.div 
                      key={`p-${pv.voterId || index}`} 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: (currentVotes.length + index) * 0.1 }}
                      className="flex items-center justify-between bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-sm border border-amber-400/30 rounded-xl px-3 py-2 shadow-lg"
                    >
                      <div className="flex items-center min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold mr-2 shadow-lg text-sm">
                          {(pv.voterName?.charAt(0) || '?').toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-white font-medium truncate text-sm">{pv.voterName}</span>
                            <Unlock className="w-3 h-3 text-amber-400 flex-shrink-0 animate-pulse" />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center flex-shrink-0 space-x-1">
                        <div className="flex items-center">
                          {[1,2,3,4,5].map(star => (
                            <Star
                              key={star}
                              className={`w-3 h-3 sm:w-4 sm:h-4 ${star <= pv.score ? 'text-yellow-400 fill-current' : 'text-white/30'}`}
                            />
                          ))}
                        </div>
                        <span className="text-sm sm:text-base font-bold text-amber-300 bg-amber-500/20 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-xs sm:text-sm">{pv.score}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
          </motion.div>

          {isMyPrompt && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card bg-blue-50 border-blue-200 text-center"
            >
              <p className="text-blue-700 font-medium">{t('voting.ownImage')}</p>
            </motion.div>
          )}

          {hasVoted && !isMyPrompt && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card bg-green-50 border-green-200 text-center"
            >
              <p className="text-green-700 font-medium">{t('voting.alreadyVoted')}</p>
            </motion.div>
          )}
        </>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card text-center bg-blue-50 border-blue-200"
        >
          <div className="space-y-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
              <Users className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-blue-800 mb-2">{t('voting.allVotesDoneTitle')}</h2>
              <p className="text-blue-700">{t('voting.allVotesDoneSubtitle')}</p>
            </div>
          </div>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <p className="text-red-600 text-center">{error}</p>
        </motion.div>
      )}

      {/* Fullscreen Image Modal */}
      <FullscreenImageModal
        isOpen={showFullscreenImage}
        onClose={() => setShowFullscreenImage(false)}
        imageUrl={currentPrompt?.imageUrl || ''}
        imageAlt={`Voting image: ${currentPromptOwner?.name}'s creation`}
        promptText={currentPrompt?.text}
        playerName={currentPromptOwner?.name}
        roundNumber={currentPrompt?.roundNumber}
      />

      {/* Room Closed Modal */}
      <RoomClosedModal
        isOpen={showRoomClosed}
        onClose={() => {
          try {
            if (roomId) {
              realtimeService.cleanupRoom(roomId)
              sessionStorage.removeItem(`joinedBroadcast:${roomId}`)
            }
          } finally {
            navigate('/')
          }
        }}
      />
    </div>
  )
}

